import { isTauri } from './tauri';
import { EXPORT_FORMATS } from './collectionExport';

function saveFiltersForFormat(format) {
  switch (format) {
    case EXPORT_FORMATS.openapi_yaml:
      return [{ name: 'OpenAPI YAML', extensions: ['yaml', 'yml'] }];
    case EXPORT_FORMATS.openapi_json:
      return [{ name: 'OpenAPI JSON', extensions: ['json'] }];
    default:
      return [{ name: 'Postman Collection', extensions: ['json'] }];
  }
}

function acceptTypesForFormat(format) {
  switch (format) {
    case EXPORT_FORMATS.openapi_yaml:
      return { 'application/x-yaml': ['.yaml', '.yml'], 'text/yaml': ['.yaml', '.yml'] };
    case EXPORT_FORMATS.openapi_json:
      return { 'application/json': ['.json'] };
    default:
      return { 'application/json': ['.json'] };
  }
}

function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function saveViaTauriDialog({ content, filename, format }) {
  const { save } = await import('@tauri-apps/plugin-dialog');
  const { writeTextFile } = await import('@tauri-apps/plugin-fs');

  const filePath = await save({
    defaultPath: filename,
    title: 'Save export',
    filters: saveFiltersForFormat(format),
  });

  if (filePath == null) {
    return { saved: false, cancelled: true };
  }

  await writeTextFile(filePath, content);
  return { saved: true, cancelled: false, path: filePath };
}

async function saveViaBrowserPicker({ content, filename, format }) {
  if (typeof window.showSaveFilePicker !== 'function') {
    downloadBlob(content, filename, 'text/plain');
    return { saved: true, cancelled: false, usedFallbackDownload: true };
  }

  try {
    const filters = saveFiltersForFormat(format);
    const handle = await window.showSaveFilePicker({
      suggestedName: filename,
      types: filters.map((f) => ({
        description: f.name,
        accept: acceptTypesForFormat(format),
      })),
    });
    const writable = await handle.createWritable();
    await writable.write(content);
    await writable.close();
    return { saved: true, cancelled: false };
  } catch (err) {
    if (err?.name === 'AbortError') {
      return { saved: false, cancelled: true };
    }
    throw err;
  }
}

/**
 * Prompt for save location and write export file.
 * @returns {Promise<{ saved: boolean, cancelled?: boolean, path?: string }>}
 */
export async function saveExportFile({ content, filename, mime, format }) {
  if (isTauri()) {
    return saveViaTauriDialog({ content, filename, format });
  }
  return saveViaBrowserPicker({ content, filename, mime, format });
}

async function saveBundleViaTauri({ files, dirName }) {
  const { open } = await import('@tauri-apps/plugin-dialog');
  const { writeTextFile, mkdir } = await import('@tauri-apps/plugin-fs');

  const baseDir = await open({
    directory: true,
    // Nested writes below: without `recursive` the dialog grants only the
    // folder's direct children (the $HOME/** grant used to hide that).
    recursive: true,
    title: 'Choose a folder to export into',
  });
  if (baseDir == null) return { saved: false, cancelled: true };

  const root = `${baseDir}/${dirName}`;
  await mkdir(root, { recursive: true });
  for (const file of files) {
    const fullPath = `${root}/${file.path}`;
    const parent = fullPath.slice(0, fullPath.lastIndexOf('/'));
    if (parent && parent !== root) await mkdir(parent, { recursive: true });
    await writeTextFile(fullPath, file.content);
  }
  return { saved: true, cancelled: false, path: root };
}

async function saveBundleViaBrowser({ files, dirName }) {
  // Directory picker (Chromium) writes the tree directly; otherwise fall back to
  // downloading each file (flattened names) so nothing is silently lost.
  if (typeof window.showDirectoryPicker === 'function') {
    try {
      const root = await window.showDirectoryPicker({ mode: 'readwrite' });
      const dir = await root.getDirectoryHandle(dirName, { create: true });
      for (const file of files) {
        const segments = file.path.split('/');
        const name = segments.pop();
        let cursor = dir;
        for (const seg of segments) {
          cursor = await cursor.getDirectoryHandle(seg, { create: true });
        }
        const handle = await cursor.getFileHandle(name, { create: true });
        const writable = await handle.createWritable();
        await writable.write(file.content);
        await writable.close();
      }
      return { saved: true, cancelled: false };
    } catch (err) {
      if (err?.name === 'AbortError') return { saved: false, cancelled: true };
      throw err;
    }
  }

  for (const file of files) {
    downloadBlob(file.content, `${dirName}__${file.path.replace(/\//g, '__')}`, 'text/plain');
  }
  return { saved: true, cancelled: false, usedFallbackDownload: true };
}

/**
 * Write a multi-file export bundle (e.g. a Bruno collection directory).
 * @param {{ files: Array<{ path: string, content: string }>, dirName: string }} params
 * @returns {Promise<{ saved: boolean, cancelled?: boolean, path?: string }>}
 */
export async function saveExportBundle({ files, dirName }) {
  const safeDir = String(dirName || 'export').replace(/[^\w.-]+/g, '_');
  if (isTauri()) {
    return saveBundleViaTauri({ files, dirName: safeDir });
  }
  return saveBundleViaBrowser({ files, dirName: safeDir });
}
